import type { AvailableProjectWorktreesResult } from "@afar-research/contracts";
import { Radio as RadioPrimitive } from "@base-ui/react/radio";
import { FolderGitIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { RadioGroup } from "./ui/radio-group";

type AvailableWorktree = AvailableProjectWorktreesResult["worktrees"][number];

export function resolveAvailableWorktreeName(worktree: AvailableWorktree): string {
  const managedName = worktree.name?.trim();
  if (managedName) return managedName;
  const pathName = /([^\\/]+)[\\/]*$/.exec(worktree.path.trim())?.[1]?.trim();
  return pathName || worktree.branch?.trim() || "Existing worktree";
}

export function BranchToolbarExistingWorktreeDialog(props: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly activeWorktreePath: string | null;
  readonly availableWorktrees: AvailableProjectWorktreesResult["worktrees"];
  readonly onConfirm: (worktree: AvailableWorktree) => void;
}) {
  const [selectedPath, setSelectedPath] = useState<string | null>(props.activeWorktreePath);

  useEffect(() => {
    if (!props.open) return;
    setSelectedPath(
      props.availableWorktrees.some((worktree) => worktree.path === props.activeWorktreePath)
        ? props.activeWorktreePath
        : null,
    );
  }, [props.activeWorktreePath, props.availableWorktrees, props.open]);

  const selectedWorktree =
    props.availableWorktrees.find((worktree) => worktree.path === selectedPath) ?? null;

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>Use existing worktree</DialogTitle>
          <DialogDescription>
            Choose a worktree that is already registered for this project.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          {props.availableWorktrees.length === 0 ? (
            <p className="text-sm text-muted-foreground">No existing worktrees are available.</p>
          ) : (
            <RadioGroup
              aria-label="Existing worktree"
              value={selectedPath ?? ""}
              onValueChange={setSelectedPath}
              className="gap-2"
            >
              {props.availableWorktrees.map((worktree) => {
                const selected = worktree.path === selectedPath;
                const displayName = resolveAvailableWorktreeName(worktree);
                return (
                  <RadioPrimitive.Root
                    key={worktree.path}
                    value={worktree.path}
                    aria-label={`${displayName} — ${worktree.path}`}
                    className={cn(
                      "flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left outline-none transition-colors",
                      "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                      selected ? "border-primary bg-primary/8" : "border-border hover:bg-muted/50",
                    )}
                  >
                    <FolderGitIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm font-medium">{displayName}</span>
                      <span
                        className="truncate font-mono text-[11px] text-muted-foreground"
                        title={worktree.path}
                      >
                        {worktree.path}
                      </span>
                    </span>
                  </RadioPrimitive.Root>
                );
              })}
            </RadioGroup>
          )}
        </DialogPanel>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={selectedWorktree === null}
            onClick={() => {
              if (!selectedWorktree) return;
              props.onConfirm(selectedWorktree);
              props.onOpenChange(false);
            }}
          >
            Use existing worktree
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
